/**
 * Broker Breakdown Component (Web)
 * Displays portfolio breakdown by broker account
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this component only displays pre-calculated data.
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { BrokerAccountSummary } from '@shared/types/portfolio';
import { BrokerAccount } from '@shared/types/brokerAccount';
import { formatCurrency, formatPercentage } from '@shared/utils/formatters';
import { BROKER_DISPLAY_NAMES } from '@shared/utils/constants';

interface BrokerBreakdownProps {
  brokerAccounts: BrokerAccountSummary[];
  allBrokerAccounts?: BrokerAccount[]; // Accounts from accounts table
  syncInProgress?: Record<string, boolean>;
  onSync?: (brokerAccountId: string) => Promise<void>;
}

export function BrokerBreakdown({
  brokerAccounts,
  allBrokerAccounts = [],
  syncInProgress: _syncInProgress,
  onSync: _onSync,
}: BrokerBreakdownProps) {
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(brokerAccounts.map((acc) => acc.broker_account_id))
  );

  const handleAccordionChange = (accountId: string) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded((prev) => {
      const newExpanded = new Set(prev);
      if (isExpanded) {
        newExpanded.add(accountId);
      } else {
        newExpanded.delete(accountId);
      }
      return newExpanded;
    });
  };

  return (
    <>
      {brokerAccounts.map((account) => {
        const isProfit = account.unrealized_pnl >= 0;
        const isDayProfit = account.day_change >= 0;
        const isExpanded = expanded.has(account.broker_account_id);

        // Find the account from accounts table using broker_account_id
        // This ensures we get account_name and broker_account_id from accounts table
        const accountFromTable = allBrokerAccounts.find(
          acc => acc.broker_account_id === account.broker_account_id || acc.id === account.broker_account_id
        );
        
        // Account Name MUST come from accounts table (broker-integration-service)
        // Only use accountFromTable, don't fallback to accountNameMap which might be stale
        const accountName = accountFromTable?.account_name || '';
        // Account ID is the broker's account identifier (from external broker system)
        // Always use broker_account_id from accounts table to ensure it's the actual broker ID, not UUID
        const accountId = accountFromTable?.broker_account_id || account.broker_account_id; // Broker's account identifier (String)
        const brokerType = account.broker_type || account.broker_name || accountFromTable?.broker_name || '';
        const brokerDisplayName = BROKER_DISPLAY_NAMES[brokerType] || brokerType;
        
        return (
          <Accordion
            key={account.broker_account_id}
            expanded={isExpanded}
            onChange={handleAccordionChange(account.broker_account_id)}
            sx={{ 
              mb: 2.5, 
              '&:before': { display: 'none' }, 
              '&:last-child': { mb: 0 },
              borderRadius: 2,
              border: '1px solid rgba(4, 55, 123, 0.12)',
              boxShadow: '0 2px 4px rgba(4, 55, 123, 0.06)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(4, 55, 123, 0.1)',
              },
              '&.Mui-expanded': {
                boxShadow: '0 4px 12px rgba(4, 55, 123, 0.12)',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#04377b' }} />}
              sx={{
                px: 2.5,
                py: 1.5,
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
                '&:hover': {
                  bgcolor: 'rgba(4, 55, 123, 0.02)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  {/* Account Name, Account ID and Broker Name - All on same line with labels */}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                    {/* Account Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          fontWeight: 600,
                        }}
                      >
                        Account Name:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          color: 'text.primary',
                          fontWeight: 600,
                          bgcolor: 'rgba(4, 55, 123, 0.08)',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                        }}
                      >
                        {accountName || brokerDisplayName}
                      </Typography>
                    </Box>
                    
                    {/* Account ID */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          fontWeight: 600,
                        }}
                      >
                        Account ID:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          color: 'text.primary',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          bgcolor: 'rgba(4, 55, 123, 0.08)',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                        }}
                      >
                        {accountId}
                      </Typography>
                    </Box>
                    
                    {/* Broker */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          fontWeight: 600,
                        }}
                      >
                        Broker:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          color: 'text.primary',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          bgcolor: 'rgba(4, 55, 123, 0.08)',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                        }}
                      >
                        {brokerDisplayName}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#04377b',
                      fontSize: '1.1rem',
                    }}
                  >
                    {formatCurrency(account.market_value)}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2.5, py: 3 }}>
              <Grid container spacing={3} sx={{ display: 'flex' }}>
              <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid rgba(4, 55, 123, 0.1)',
                    boxShadow: '0 2px 4px rgba(4, 55, 123, 0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(4, 55, 123, 0.12)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      Market value
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.15rem',
                        color: '#04377b',
                        mb: 1,
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCurrency(account.market_value)}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mt: 'auto',
                        display: 'block', 
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {account.holdings_count} holding{account.holdings_count !== 1 ? 's' : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid rgba(4, 55, 123, 0.1)',
                    boxShadow: '0 2px 4px rgba(4, 55, 123, 0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(4, 55, 123, 0.12)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      Invested
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.15rem',
                        color: '#04377b',
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCurrency(account.invested_value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: `1px solid ${isProfit ? 'rgba(46, 125, 50, 0.2)' : 'rgba(211, 47, 47, 0.2)'}`,
                    boxShadow: `0 2px 4px ${isProfit ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)'}`,
                    background: isProfit 
                      ? 'linear-gradient(to bottom, rgba(46, 125, 50, 0.03) 0%, rgba(46, 125, 50, 0.01) 100%)'
                      : 'linear-gradient(to bottom, rgba(211, 47, 47, 0.03) 0%, rgba(211, 47, 47, 0.01) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${isProfit ? 'rgba(46, 125, 50, 0.15)' : 'rgba(211, 47, 47, 0.15)'}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      Return on investment
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {isProfit ? (
                        <TrendingUpIcon sx={{ fontSize: '1.3rem', color: '#2e7d32' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.3rem', color: '#d32f2f' }} />
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.15rem',
                          color: isProfit ? '#2e7d32' : '#d32f2f',
                          lineHeight: 1.2,
                        }}
                      >
                        {formatPercentage(account.unrealized_pnl_percentage)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isProfit ? '#2e7d32' : '#d32f2f',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {formatCurrency(account.unrealized_pnl, { showSign: true })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: `1px solid ${isProfit ? 'rgba(46, 125, 50, 0.2)' : 'rgba(211, 47, 47, 0.2)'}`,
                    boxShadow: `0 2px 4px ${isProfit ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)'}`,
                    background: isProfit 
                      ? 'linear-gradient(to bottom, rgba(46, 125, 50, 0.03) 0%, rgba(46, 125, 50, 0.01) 100%)'
                      : 'linear-gradient(to bottom, rgba(211, 47, 47, 0.03) 0%, rgba(211, 47, 47, 0.01) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${isProfit ? 'rgba(46, 125, 50, 0.15)' : 'rgba(211, 47, 47, 0.15)'}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      Unrealized P&L
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {isProfit ? (
                        <TrendingUpIcon sx={{ fontSize: '1.3rem', color: '#2e7d32' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.3rem', color: '#d32f2f' }} />
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.15rem',
                          color: isProfit ? '#2e7d32' : '#d32f2f',
                          lineHeight: 1.2,
                        }}
                      >
                        {formatCurrency(account.unrealized_pnl, { showSign: true })}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isProfit ? '#2e7d32' : '#d32f2f',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {formatPercentage(account.unrealized_pnl_percentage)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: `1px solid ${isDayProfit ? 'rgba(46, 125, 50, 0.2)' : 'rgba(211, 47, 47, 0.2)'}`,
                    boxShadow: `0 2px 4px ${isDayProfit ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)'}`,
                    background: isDayProfit 
                      ? 'linear-gradient(to bottom, rgba(46, 125, 50, 0.03) 0%, rgba(46, 125, 50, 0.01) 100%)'
                      : 'linear-gradient(to bottom, rgba(211, 47, 47, 0.03) 0%, rgba(211, 47, 47, 0.01) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${isDayProfit ? 'rgba(46, 125, 50, 0.15)' : 'rgba(211, 47, 47, 0.15)'}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      Day change
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {isDayProfit ? (
                        <TrendingUpIcon sx={{ fontSize: '1.3rem', color: '#2e7d32' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.3rem', color: '#d32f2f' }} />
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.15rem',
                          color: isDayProfit ? '#2e7d32' : '#d32f2f',
                          lineHeight: 1.2,
                        }}
                      >
                        {formatCurrency(account.day_change, { showSign: true })}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDayProfit ? '#2e7d32' : '#d32f2f',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {formatPercentage(account.day_change_percentage)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
}

